/**
 * useDraggableFAB(key, defaultPos)
 * --------------------------------
 * Hook for a Floating Action Button (FAB) that can be repositioned by the user.
 * Persists the (x, y) in localStorage under `dyd:fab:<key>` so the location
 * survives reloads. Returns:
 *   - style: an inline style object you spread onto the FAB root.
 *   - bind: a single prop bag you spread onto the FAB ({ onPointerDown, onPointerMove, onPointerUp }).
 *   - dragging: boolean while user is currently dragging (>5px movement).
 *   - reset(): clears the persisted position.
 *
 * Behavior:
 *   - Drag threshold of 5px before "dragging" becomes true, so a quick tap still triggers onClick.
 *   - Position is clamped to viewport.
 *   - Works on touch and mouse via Pointer Events.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "dyd:fab:";
const DRAG_THRESHOLD = 5;

function loadPos(key) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.x === "number" && typeof v?.y === "number") return v;
  } catch (_) { /* ignore */ }
  return null;
}

function savePos(key, pos) {
  try { localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(pos)); } catch (_) { /* ignore */ }
}

export default function useDraggableFAB(key, defaultPos = { right: 16, bottom: 16 }) {
  // pos.x/.y are in CSS top-left coords (px). If null, fall back to defaultPos (right/bottom).
  const [pos, setPos] = useState(() => loadPos(key));
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);
  const elRef = useRef(null);

  const handlePointerDown = useCallback((e) => {
    if (!elRef.current) return;
    const rect = elRef.current.getBoundingClientRect();
    startRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!startRef.current) return;
    const dx = Math.abs(e.clientX - startRef.current.startX);
    const dy = Math.abs(e.clientY - startRef.current.startY);
    if (!startRef.current.moved && (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD)) {
      startRef.current.moved = true;
      setDragging(true);
    }
    if (!startRef.current.moved) return;
    const w = elRef.current?.offsetWidth || 60;
    const h = elRef.current?.offsetHeight || 60;
    const maxX = window.innerWidth - w - 4;
    const maxY = window.innerHeight - h - 4;
    const x = Math.max(4, Math.min(maxX, e.clientX - startRef.current.offsetX));
    const y = Math.max(4, Math.min(maxY, e.clientY - startRef.current.offsetY));
    setPos({ x, y });
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (startRef.current?.moved) {
      // commit position
      if (pos) savePos(key, pos);
      // prevent click after drag
      e.preventDefault?.();
      e.stopPropagation?.();
    }
    setDragging(false);
    startRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_) { /* noop */ }
  }, [key, pos]);

  // Re-clamp on resize (e.g., rotation, sidebar opening on desktop)
  useEffect(() => {
    if (!pos) return;
    const onResize = () => {
      if (!elRef.current) return;
      const w = elRef.current.offsetWidth || 60;
      const h = elRef.current.offsetHeight || 60;
      const x = Math.max(4, Math.min(window.innerWidth - w - 4, pos.x));
      const y = Math.max(4, Math.min(window.innerHeight - h - 4, pos.y));
      if (x !== pos.x || y !== pos.y) {
        setPos({ x, y });
        savePos(key, { x, y });
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos, key]);

  const style = pos
    ? { position: "fixed", left: `${pos.x}px`, top: `${pos.y}px`, right: "auto", bottom: "auto", touchAction: "none", cursor: dragging ? "grabbing" : "grab" }
    : { position: "fixed", ...defaultPos, touchAction: "none", cursor: dragging ? "grabbing" : "grab" };

  const bind = {
    ref: elRef,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerUp,
  };

  const reset = useCallback(() => {
    try { localStorage.removeItem(STORAGE_PREFIX + key); } catch (_) { /* ignore */ }
    setPos(null);
  }, [key]);

  return { style, bind, dragging, reset, pos };
}
